
import React, { useContext, useEffect, useRef, useState, } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SpinnerComponent } from 'react-element-spinner'
import {Card, CardBody, CardFooter, Col, Container, FormGroup, Label, Row} from 'reactstrap'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import utc from 'dayjs/plugin/utc'
import timeZone from 'dayjs/plugin/timezone'
dayjs.extend(localizedFormat)
dayjs.extend(utc)
dayjs.extend(timeZone)

import { AuctionsContext } from '../../../contexts/AuctionsProvider'
import AuctionsService from '../../../services/AuctionsService'
import { SessionContext } from '../../../contexts/SessionProvider'
import Paginator from '../../../components/Paginator/Paginator'
import SiteService from '../../../services/SiteService'
import { subscribe, trigger, unsubscribe } from '../../../events/customEvents'
import AuctionListGroupItem from '../../../components/Auctions/AuctionListGroupItem'
import PageTitleContextMenu from '../../../components/PageTitles/PageTitleContextMenu'
import PlaceMemberBidModal from '../../../components/Modals/PlaceMemberBidModal'
import WatchlistsService from '../../../services/WatchlistsService'
import PlaceMemberBuyNowModal from '../../../components/Modals/PlaceMemberBuyNowModal'
import CounterBidModal from '../../../components/Modals/CounterBidModal'
import OemsService from '../../../services/OemsService'
import { useOnScreen } from '../../../components/Observers/AuctionListingIntersectionObserver'
import asDescribedImage from '../../../assets/images/brand/as-described.png'
import conditionReportImage from '../../../assets/images/brand/condition-report.png'
import firstRunImage from '../../../assets/images/brand/first-run.png'

let timerIntervalId = null

const DealerAuctionsListing = () =>
{
	const navigate = useNavigate()
	const location = useLocation()
	const { t } = useTranslation()
	const [isBusy, setIsBusy] = useState(false)
	const sessionContext = useContext(SessionContext)
	const auctionsContext = useContext(AuctionsContext)
	const [showMemberBidModal, setShowMemberBidModal] = useState(false)
	const [bidAuctionListItem, setBidAuctionListItem] = useState({ auctionTier: { auctionTierExpiryInSeconds: -1 } })
	const [showMemberBuyNowModal, setShowMemberBuyNowModal] = useState(false)
	const [searchFilters, setSearchFilters] = useState({ makes: [], models: [], provinces: [], vehicleYears: [], vehicleOdometers: [], buyNowPrices: [], auctionsState: 'Active' })
	const [showCounterBidModal, setShowCounterBidModal] = useState(false)
	const [auctionTierMemberBid, setAuctionTierMemberBid] = useState(undefined)
	const [memberBidCounters, setMemberBidCounters] = useState([])
	const [componentViewSettings, setComponentViewSettings] = useState([])

	const containerRef = useRef(null)
	const [visible, observeEl, unobserveEl] = useOnScreen({
		auctionsContext,
		AuctionsService,
		items: auctionsContext.memberPagingResult.pageCollection,
		rootMargin: "-400px"
	})

	const resolveRouteSearchRequest = (previousSearchRequest) =>
	{
		if (location.search.includes('view=auctions'))
		{
			return {
				...previousSearchRequest,
				viewWatchlist: false,
			}
		}

		if (location.state && location.state.viewWatchlist === true)
		{
			return {
				...previousSearchRequest,
				viewWatchlist: true,
			}
		}

		if (!location.state)
		{
			return {
				...previousSearchRequest,
				viewWatchlist: false,
			}
		}

		return previousSearchRequest
	}

	useEffect(() =>
	{
		const nextSearchRequest = resolveRouteSearchRequest(auctionsContext.memberSearchRequest)
		auctionsContext.setMemberSearchRequest(nextSearchRequest)

		retrieveComponentViewSettings('DealerAuctionsListing', nextSearchRequest)
		
		subscribe('placeMemberBidModalClosed', handlePlaceMemberBidModalClosed)
		subscribe('placeMemberBuyNowModalClosed', handlePlaceMemberBuyNowModalClosed)
		subscribe('counterMemberBidModalClosed', handleCounterMemberBidModalClosed)
		subscribe('auctionListGroupItemIntersection', handleAuctionListGroupItemIntersection)
		
		return () =>
		{
			clearInterval(timerIntervalId)
			
			unsubscribe('placeMemberBidModalClosed', handlePlaceMemberBidModalClosed)
			unsubscribe('placeMemberBuyNowModalClosed', handlePlaceMemberBuyNowModalClosed)
			unsubscribe('counterMemberBidModalClosed', handleCounterMemberBidModalClosed)
			unsubscribe('auctionListGroupItemIntersection', handleAuctionListGroupItemIntersection)
		}
	}, [location.pathname, location.search, location.state])
	
	const retrieveComponentViewSettings = (componentName, searchRequestOverride) =>
	{
		setIsBusy(true)
		OemsService
			.getComponentViewSettings(componentName, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)

				setComponentViewSettings(result.data.actionResult)

				retrieveMemberSearchResults(searchRequestOverride ?? auctionsContext.memberSearchRequest)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				setComponentViewSettings([])
				retrieveMemberSearchResults(searchRequestOverride ?? auctionsContext.memberSearchRequest)
				SiteService.displayApiError(error)
			})
	}

	const retrieveMemberSearchResults = (searchRequest) =>
	{
        clearInterval(timerIntervalId)

		setIsBusy(true)
		AuctionsService
			.memberAuctionSearch(searchRequest, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)

				let _itemIdx = 0
				let _pagingResult = result.data.actionResult
				let _pageCollection = []
				const distinctPageCollection = [...new Map(_pagingResult.pageCollection.map(pageItem => [pageItem.auctionId, pageItem])).values()]
				distinctPageCollection.forEach((pageItem) =>
				{
					pageItem.itemIdx = _itemIdx

					if (!pageItem.primaryDisplayImageUrl && (pageItem.vehicleInspectionPictures && pageItem.vehicleInspectionPictures.length > 0))
					{
						pageItem.primaryDisplayImageUrl = pageItem.vehicleInspectionPictures[0]
					}

					const _expiryCountdown =
					{
						timeDisplay: SiteService.setExpiryTimeDisplay(pageItem.auctionTier.auctionTierExpiryInSeconds),
						secondsRemaining: (pageItem.auctionTier.auctionTierExpiryInSeconds > 0) ? pageItem.auctionTier.auctionTierExpiryInSeconds : 0,
					}

					// let auctionTierExpiryTime = dayjs(pageItem.auctionTier.tierUtcEndTime).tz(dayjs.tz.guess()).format('llll')
					_pageCollection.push({
						...pageItem,
						..._expiryCountdown,
					})

					_itemIdx++
				})

				
				_pageCollection.sort((a, b) =>
				{
					const aHasCounter = (a.auctionTier && a.auctionTier.yourBid && a.auctionTier.yourBid.counterBid) ? 1 : 0
					const bHasCounter = (b.auctionTier && b.auctionTier.yourBid && b.auctionTier.yourBid.counterBid) ? 1 : 0
					return bHasCounter - aHasCounter
				})
				_pageCollection.forEach((pageItem, index) => { pageItem.itemIdx = index })

				_pagingResult.pageCollection = _pageCollection
				// NOTE: keep the server-supplied totalRecords (the distinct count across ALL
				// pages). Overwriting it with _pageCollection.length collapsed the total to the
				// current page size (e.g. 25 -> 15), which made the Paginator render a single
				// page and left the remaining records unreachable. The client-side dedup above
				// only guards against the same auctionId appearing twice within one page and
				// must not replace the cross-page total used to compute the page count.
				auctionsContext.setMemberPagingResult(_pagingResult)

				timerIntervalId = setInterval(countDownAuctionTierExpiry, 1000)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}

	const countDownAuctionTierExpiry = () =>
	{
		auctionsContext.setMemberPagingResult((previousPagingResult) =>
		{
			let currentPagingResult = { ...previousPagingResult }
			let totalSecondsRemaining = 0

			currentPagingResult.pageCollection.forEach((pageItem) =>
			{
				if (pageItem.secondsRemaining > 1)
				{
					let secondsRemaining = pageItem.secondsRemaining - 1
					pageItem.timeDisplay = SiteService.setExpiryTimeDisplay(secondsRemaining)
					pageItem.secondsRemaining = secondsRemaining

					totalSecondsRemaining = totalSecondsRemaining + secondsRemaining
				}
			})

			return currentPagingResult
		})
	}

	const handleReload = (searchRequest) =>
	{
		clearInterval(timerIntervalId)
		retrieveMemberSearchResults(searchRequest)
	}

	const handlePageChange = (pageIndex) =>
	{
		auctionsContext.setMemberSearchRequest((previousMemberSearchRequest) =>
		{
			let currentMemberSearchRequest = { ...previousMemberSearchRequest }
			currentMemberSearchRequest.pageIndex = pageIndex

			retrieveMemberSearchResults(currentMemberSearchRequest)

			return currentMemberSearchRequest
		})
	}

	const handleAddToWatchList = (pageItem) =>
	{
		setIsBusy(true)
		WatchlistsService
			.addMemberWatchlist(pageItem.auctionTier.auctionTierMemberId, pageItem.auctionTier.auctionTierId, pageItem.auctionId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)

				trigger('raiseNotification', { title: 'Add Success', message: 'The watchlist item was added.', type: 'success', duration: 5 })
				pageItem.auctionTier.isOnMemberWatchlist = true
				retrieveMemberSearchResults(auctionsContext.memberSearchRequest)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}

	const handleViewDetails = (auctionListItem) =>
	{
		auctionsContext.setSelectedAuctionListItem((previousAuctionItem) =>
		{
			return auctionListItem
		})

		auctionsContext.setBackRoute((previousBackRoute) =>
		{
			return '/secure/dealer-auction-listing'
		})

		navigate('/secure/dealer-vehicle-detail')
	}

	const handlePlaceBuyNow = (auctionListItem) =>
	{
		setBidAuctionListItem((previousAuctionItem) =>
		{
			return auctionListItem
		})

		setShowMemberBuyNowModal((previousShowMemberBuyNowModal) =>
		{
			return true
		})
	}

	const handlePlaceBid = (auctionListItem) =>
	{
		setBidAuctionListItem((previousBidAuctionItem) =>
		{
			let currentBidAuctionItem = { ...auctionListItem }

			if (currentBidAuctionItem.auctionTier.yourBid && currentBidAuctionItem.auctionTier.yourBid.counterBid)
			{
				setupCounterBids(currentBidAuctionItem)
			}
			else
			{
				setShowMemberBidModal(true)
			}

			return currentBidAuctionItem
		})
	}

	const setupCounterBids = (auctionListItem) =>
	{
		setAuctionTierMemberBid((previousAuctionTierMemberBid) =>
		{
			return auctionListItem.auctionTier.yourBid
		})

		setIsBusy(true)
		AuctionsService
			.getMemberBidCounters(auctionListItem.auctionTier.yourBid.auctionTierMemberBidId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)

				setMemberBidCounters(result.data.actionResult)
				setShowCounterBidModal(true)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}

	const handleCounterMemberBidModalClosed = (event) =>
	{
		setShowCounterBidModal(false)
		retrieveMemberSearchResults(auctionsContext.memberSearchRequest)
	}

	const handleRemoveFromWatchList = (pageItem) =>
	{
		setIsBusy(true)
		WatchlistsService
			.removeMemberWatchlist(pageItem.auctionTier.auctionTierMemberId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)

				trigger('raiseNotification', { title: 'Removal Success', message: 'The watchlist item was removed.', type: 'success', duration: 5 })
				pageItem.isOnMemberWatchlist = false
				
				retrieveMemberSearchResults(auctionsContext.memberSearchRequest)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}

	const handleAuctionListGroupItemIntersection = (event) =>
	{
		event.stopImmediatePropagation()
		event.preventDefault()
		try
		{
			AuctionsService
				.addAuctionListImpression(event.detail.auctionTierMemberId, sessionContext.loginSession.user.userSession.sessionKey)
				.then(result =>
				{
					sessionContext.updateSession(result.data.loginSession)
				})
				.catch((error) =>
				{
					console.log(error)
				})
		}
		catch {}
	}

	const handlePlaceMemberBidModalClosed = (event) =>
	{
		setShowMemberBidModal(false)
		if (event.detail.bidPlaced === true)
		{
			retrieveMemberSearchResults(auctionsContext.memberSearchRequest)
		}
	}

	const handlePlaceMemberBuyNowModalClosed = (event) =>
	{
		setShowMemberBuyNowModal(false)
		if (event.detail.buyNowPlaced === true)
		{
            navigate('/secure/owned-vehicles')
		}
        else
        {
            retrieveMemberSearchResults(auctionsContext.memberSearchRequest)
        }
	}

    const handleStopViewingWatchlist = () =>
    {
        auctionsContext.setMemberSearchRequest((previousSearchRequest) =>
        {
            const currentSearchRequest = { ...previousSearchRequest }
            currentSearchRequest.viewWatchlist = false

            retrieveMemberSearchResults(currentSearchRequest)

            return currentSearchRequest
        })
    }

	return (
		<>
			<SpinnerComponent
				loading={ isBusy }
				position="global"
				color="#3578e5"
				secondaryColor="#f3f3f3"
				backgroundColor="#d3d3d3"
				spinnerType="circle-dots"
			/>
			{/*
				---- NOTE ----
				For conditional display of the back button if the user
				did not come from the search component
				<Button {...(condition ? {bsStyle: 'success'} : {})} />
			*/}
			<PageTitleContextMenu
				heading={ auctionsContext.memberSearchRequest.viewWatchlist ? t('Watchlist.Vehicle.Title') : t('Auctions.Listing.Title') }
				subheading=""
				icon="pe-7s-ticket icon-gradient bg-premium-dark"
				headerOptions={
					{
						showBack: false,
						backCommand: null,
						showReload: true,
						reloadCommand: () => { handleReload(auctionsContext.memberSearchRequest) },
						showResort: false,
						resortCommand: null,
						showFilter: false,
						filterCommand: null
					}
				}
				searchFilters={ searchFilters }
			/>
			<Container className="mt--6" fluid>
				<Row>
					<div className="col">
						<Card>
							<CardBody>
                                {
                                    auctionsContext.memberSearchRequest.viewWatchlist &&
                                    <Row >
                                        <Col md="12" style={{ textAlign: 'right' }}>
                                            <FormGroup style={{ marginBottom: '0px', paddingTop: '5px' }}>
                                                <input
                                                    name="disabled"
                                                    type="checkbox"
                                                    checked={ auctionsContext.memberSearchRequest.viewWatchlist }
                                                    onChange={ () => handleStopViewingWatchlist() }
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                <Label style={{ marginLeft: '3px' }}>{ t('Auctions.Listing.Viewing_Watchlist') }</Label>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                }
								<div className="auction-list-items-container" ref={containerRef}>
									{
										auctionsContext.memberPagingResult.pageCollection.map((pageItem, pgItemIdx) => (
											<AuctionListGroupItem
												key={pgItemIdx}
												idx={pgItemIdx}
												auctionListItem={ pageItem }
												showVehiclePicCollection={ true }
												removeFromWatchListClicked={ handleRemoveFromWatchList }
												viewDetailsClicked={ handleViewDetails }
												placeBuyNowClicked={ handlePlaceBuyNow }
												placeBidClicked={ handlePlaceBid }
												addToWatchlistClicked={ handleAddToWatchList }
												componentViewSettings={ componentViewSettings }
												visible={visible && visible[`auction-list-item-${pgItemIdx}`]}
												observe={observeEl}
												unobserve={unobserveEl}
												asDescribedImage={ asDescribedImage }
												conditionReportImage={ conditionReportImage }
												firstRunImage={ firstRunImage }
											/>
										))
									}
								</div>
								{
									auctionsContext.memberPagingResult.totalRecords === 0 &&
									<Row style={{ marginTop: '20px' }}>
										<Col md="12" style={{ textAlign: 'center' }}>
											<Label>{ t('Auctions.Listing.No_Results') }</Label>
										</Col>
									</Row>
								}
							</CardBody>
							<CardFooter className="py-4">
								{
									auctionsContext.memberPagingResult.pageCollection.length > 0 &&
									<nav aria-label="...">
										<Paginator
											currentPage={ auctionsContext.memberSearchRequest.pageIndex }
											pages={ parseInt(((auctionsContext.memberPagingResult.totalRecords - 1) / auctionsContext.memberSearchRequest.pageSize) + 1, 10) }
											selectPage={ handlePageChange }
										/>
									</nav>
								}
							</CardFooter>
						</Card>
					</div>
				</Row>
			</Container>

			<PlaceMemberBidModal
				showMemberBidModal={ showMemberBidModal }
				auctionListItem={ bidAuctionListItem }
			/>

			<PlaceMemberBuyNowModal
				showMemberBuyNowModal={ showMemberBuyNowModal }
				auctionListItem={ bidAuctionListItem }
			/>

			<CounterBidModal
				showCounterBidModal={ showCounterBidModal }
				auctionTierMemberBid={ auctionTierMemberBid }
				memberBidCounters={ memberBidCounters }
				auctionListItemSecondsRemaining={ bidAuctionListItem.auctionTier.auctionTierExpiryInSeconds }
			/>
		</>
	)
}

export default DealerAuctionsListing
